(() => {
  "use strict";

  // Behaviour for the homepage sections (sections/hp-*.liquid), ported from
  // assets/homepage-override.js. Each section's root carries
  // data-hpo-section="<kind>"; it is set up on load, and again whenever the
  // theme editor re-renders that section.
  if (window.hpoSections) return;
  window.hpoSections = true;

  const MOBILE = "(max-width: 749px)";

  const INIT = {
    hero: initHero,
    promos: initPromos,
    trusted: initTrusted,
    features: initFeatures,
    stories: initStories,
    categories: initCategories,
  };

  // root -> scope, so the editor's unload event can undo window listeners,
  // timers and observers a section added.
  const scopes = new WeakMap();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (e) =>
    e.target.querySelectorAll("[data-hpo-section]").forEach(initRoot),
  );
  document.addEventListener("shopify:section:unload", (e) =>
    e.target.querySelectorAll("[data-hpo-section]").forEach(destroyRoot),
  );
  document.addEventListener("shopify:block:select", (e) => {
    if (!e.target.closest("[data-hpo-section]")) return;

    e.target.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  });

  function initAll() {
    document.querySelectorAll("[data-hpo-section]").forEach(initRoot);
  }

  function initRoot(root) {
    const init = INIT[root.getAttribute("data-hpo-section")];
    if (!init || scopes.has(root)) return;

    const scope = createScope();
    scopes.set(root, scope);
    init(root, scope);
  }

  function destroyRoot(root) {
    const scope = scopes.get(root);
    if (!scope) return;

    scope.destroy();
    scopes.delete(root);
  }

  function createScope() {
    const undo = [];

    return {
      on(target, type, fn, opts) {
        target.addEventListener(type, fn, opts);
        undo.push(() => target.removeEventListener(type, fn, opts));
      },
      onDestroy(fn) {
        undo.push(fn);
      },
      destroy() {
        undo.splice(0).forEach((fn) => fn());
      },
    };
  }

  // ── sale end ────────────────────────────────────────────────────────────
  // data-hpo-end is epoch seconds, worked out in Liquid from the section's
  // "Sale ends" setting. Liquid already hides expired copy; this catches a
  // page that was cached before the sale ended.

  function saleEnd(el) {
    const seconds = Number(el && el.getAttribute("data-hpo-end"));

    return seconds > 0 ? seconds * 1000 : 0;
  }

  function initHero(root) {
    const eyebrow = root.querySelector(".hpo-hero-eyebrow[data-hpo-end]");
    const end = saleEnd(eyebrow);

    if (end && end <= Date.now()) eyebrow.remove();
  }

  // ── promo cards ─────────────────────────────────────────────────────────

  function initPromos(root, scope) {
    initCountdown(root, scope);
    equalizePromoBodies(root, scope);
    initCarousel(root, scope);
  }

  function initCountdown(root, scope) {
    const el = root.querySelector("[data-hpo-countdown]");
    const end = saleEnd(el);
    if (!el || !end) return;

    let timer = null;

    const tick = () => {
      const left = end - Date.now();

      if (left <= 0) {
        if (timer) clearInterval(timer);
        const line = el.closest(".hpo-promos-countdown");
        if (line) line.remove();
        return;
      }

      el.textContent = formatCountdown(left);
    };

    tick();
    timer = setInterval(tick, 1000);
    scope.onDestroy(() => clearInterval(timer));
  }

  function formatCountdown(ms) {
    const total = Math.floor(ms / 1000);
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  }

  function equalizePromoBodies(root, scope) {
    const bodies = root.querySelectorAll(".hpo-promo-body");
    if (!bodies.length) return;

    const apply = () => {
      bodies.forEach((el) => (el.style.minHeight = ""));

      const tallest = Math.max(
        ...Array.from(bodies, (el) => el.getBoundingClientRect().height),
      );
      bodies.forEach((el) => (el.style.minHeight = `${Math.ceil(tallest)}px`));
    };

    apply();

    if (document.fonts && document.fonts.ready)
      document.fonts.ready.then(apply);

    let resizeTimer;
    scope.on(window, "resize", () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(apply, 150);
    });
  }

  // ── carousels ───────────────────────────────────────────────────────────

  function initCarousel(root, scope) {
    const track = root.querySelector("[data-hpo-track]");
    const prev = root.querySelector("[data-hpo-prev]");
    const next = root.querySelector("[data-hpo-next]");
    if (!track || !prev || !next) return;

    const looping = track.hasAttribute("data-hpo-loop");

    const step = () => {
      const card = track.firstElementChild;
      if (!card) return track.clientWidth;

      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;

      return card.offsetWidth + gap;
    };

    prev.addEventListener("click", () => {
      track.scrollBy({ left: -step(), behavior: "smooth" });
    });
    next.addEventListener("click", () => {
      track.scrollBy({ left: step(), behavior: "smooth" });
    });

    const updateArrows = () => {
      if (looping) return;

      const maxScroll = track.scrollWidth - track.clientWidth;
      prev.hidden = track.scrollLeft <= 1;
      next.hidden = track.scrollLeft >= maxScroll - 1;
    };

    track.addEventListener("scroll", updateArrows, { passive: true });
    scope.on(window, "resize", updateArrows);
    updateArrows();

    if (looping) initLoop(track, scope);
  }

  // Endless scroll: three identical card sets, and scrolling into either outer
  // set silently jumps back to the middle one.
  function initLoop(track, scope) {
    const active = () => track.hasAttribute("data-hpo-loop");

    const setWidth = () => {
      const cards = track.children;
      const perSet = cards.length / 3;

      return cards[perSet] ? cards[perSet].offsetLeft - cards[0].offsetLeft : 0;
    };

    const recentre = () => {
      if (!active()) return;

      const width = setWidth();
      if (width > 0) track.scrollLeft = width;
    };

    const normalise = () => {
      if (!active()) return;

      const width = setWidth();
      if (width <= 0) return;

      let guard = 5;
      while (track.scrollLeft < width * 0.5 && guard--)
        track.scrollLeft += width;

      guard = 5;
      while (track.scrollLeft >= width * 1.5 && guard--)
        track.scrollLeft -= width;
    };

    let touched = false;
    ["pointerdown", "touchstart", "wheel", "keydown"].forEach((evt) =>
      track.addEventListener(evt, () => (touched = true), {
        once: true,
        passive: true,
      }),
    );

    const settleStart = () => {
      if (!touched) recentre();
    };

    recentre();
    requestAnimationFrame(settleStart);
    setTimeout(settleStart, 300);
    scope.on(window, "load", settleStart);

    let settle;
    track.addEventListener(
      "scroll",
      () => {
        clearTimeout(settle);
        settle = setTimeout(normalise, 120);
      },
      { passive: true },
    );

    let resized;
    scope.on(window, "resize", () => {
      clearTimeout(resized);
      resized = setTimeout(recentre, 150);
    });
  }

  function cloneTile(tile) {
    const clone = tile.cloneNode(true);
    clone.setAttribute("data-hpo-clone", "");
    clone.setAttribute("aria-hidden", "true");
    clone.removeAttribute("data-shopify-editor-block");

    const focusable = clone.matches("a, video") ? [clone] : [];
    focusable
      .concat(Array.from(clone.querySelectorAll("a, video")))
      .forEach((el) => (el.tabIndex = -1));

    return clone;
  }

  function initCategories(root, scope) {
    initCarousel(root, scope);
  }

  // ── feature cards ───────────────────────────────────────────────────────

  function initFeatures(root) {
    root.querySelectorAll(".hpo-feature-video").forEach((video) => {
      video.muted = true;
      const played = video.play();
      if (played && played.catch) played.catch(() => {});
    });
  }

  // ── trusted-by logos ────────────────────────────────────────────────────

  function initTrusted(root, scope) {
    const track = root.querySelector(".hpo-trusted-track");
    if (!track) return;

    const mobile = window.matchMedia(MOBILE);
    const originals = Array.from(track.children);

    const sync = () => {
      const duplicated = track.hasAttribute("data-hpo-marquee");

      if (mobile.matches && !duplicated) {
        originals.forEach((logo) => track.appendChild(cloneTile(logo)));
        track.setAttribute("data-hpo-marquee", "");
      } else if (!mobile.matches && duplicated) {
        track.removeAttribute("data-hpo-marquee");
        track
          .querySelectorAll("[data-hpo-clone]")
          .forEach((el) => el.remove());
      }
    };

    sync();
    scope.on(mobile, "change", sync);
  }

  // ── stories ─────────────────────────────────────────────────────────────

  function initStories(root, scope) {
    const track = root.querySelector(".hpo-stories-grid");
    if (!track) return;

    const mobile = window.matchMedia(MOBILE);
    const originals = Array.from(track.children);
    const state = { observer: null };

    const addClones = () => {
      if (track.hasAttribute("data-hpo-loop")) return;

      originals.forEach((tile) => track.appendChild(cloneTile(tile)));
      originals
        .slice()
        .reverse()
        .forEach((tile) =>
          track.insertBefore(cloneTile(tile), track.firstChild),
        );

      track.setAttribute("data-hpo-loop", "");
      bindStoryVideos(root, state);
    };

    const removeClones = () => {
      if (!track.hasAttribute("data-hpo-loop")) return;

      track.removeAttribute("data-hpo-loop");
      track.querySelectorAll("[data-hpo-clone]").forEach((el) => el.remove());
      track.scrollLeft = 0;
    };

    const sync = () => (mobile.matches ? addClones() : removeClones());

    sync();
    initLoop(track, scope);
    scope.on(mobile, "change", sync);
    bindStoryVideos(root, state);
    scope.onDestroy(() => state.observer && state.observer.disconnect());
  }

  // Hover plays with sound on desktop; on mobile the centred tile autoplays
  // muted and a tap toggles sound.
  function bindStoryVideos(root, state) {
    const canHover = window.matchMedia("(hover: hover)").matches;
    const autoplay = window.matchMedia(MOBILE).matches;

    if (autoplay && !state.observer && "IntersectionObserver" in window) {
      state.observer = new IntersectionObserver(onStoryVisible, {
        threshold: 0.5,
      });
    }

    root.querySelectorAll(".hpo-story-video").forEach((video) => {
      if (video.dataset.hpoBound) return;
      video.dataset.hpoBound = "1";

      if (canHover) {
        video.addEventListener("mouseenter", () => playWithSound(video));

        video.addEventListener("mouseleave", () => {
          video.pause();
          video.muted = true;
        });
      }

      video.addEventListener("click", () => {
        if (autoplay) return toggleStorySound(root, video);

        if (!video.paused) return video.pause();

        pauseOtherStories(root, video);
        playWithSound(video);
      });

      if (state.observer) state.observer.observe(video);
    });
  }

  function onStoryVisible(entries) {
    entries.forEach((entry) => {
      const video = entry.target;

      if (entry.isIntersecting) {
        const played = video.play();
        if (played && played.catch) played.catch(() => {});

        return;
      }

      video.pause();
      video.muted = true;
    });
  }

  function toggleStorySound(root, video) {
    if (!video.muted) {
      video.muted = true;

      return;
    }

    root.querySelectorAll(".hpo-story-video").forEach((other) => {
      if (other !== video) other.muted = true;
    });

    playWithSound(video);
  }

  function pauseOtherStories(root, current) {
    root.querySelectorAll(".hpo-story-video").forEach((video) => {
      if (video === current) return;

      video.pause();
      video.muted = true;
    });
  }

  // Browsers can refuse unmuted playback; fall back to muted.
  function playWithSound(video) {
    video.muted = false;

    const played = video.play();
    if (!played || !played.catch) return;

    played.catch(() => {
      video.muted = true;
      const retry = video.play();
      if (retry && retry.catch) retry.catch(() => {});
    });
  }
})();
